// src/pages/GSTRateMaster.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  OverlayTrigger,
  Button,
  Modal,
  Row,
  Col,
  Form,
} from "react-bootstrap";
import { Tooltip } from "@mui/material";
import {
  MdFilterList,
  MdAddCircle,
  MdEdit,
  MdDelete, // added for delete icon
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import DataTable from "react-data-table-component";
import loading from "../../../assets/images/common/loading.gif";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const GSTRateMaster = () => {
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const userData = sessionStorage.getItem("user");
  const userObj = JSON.parse(userData || "{}");

  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [rowsSrc, setRowsSrc] = useState([]);

  const [showFilterModal, setShowFilterModal] = useState(false);

  // expandable details
  const [gstDetails, setGstDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});

  const [showAll, setShowAll] = useState(true);

  // code types & HSN options for filter
  const [codeTypes, setCodeTypes] = useState([]);
  const [hsnSacOptions, setHsnSacOptions] = useState([]);
  const [loadingHSN, setLoadingHSN] = useState(false);

  const [form, setForm] = useState({
    code_type: "", // string like "GOODS" / "SERVICES"
    code_type_id: "", // numeric id
    hsn_sac_master_id: "", // selected HSN/SAC master id
    from_date: "",
    to_date: "",
  });

  const readOnlyRoles = ["Class Teacher", "Teacher", "Class Incharge"];
  const canSubmit = !readOnlyRoles.includes(userObj.role_name?.trim?.() || "");

  // ---------- helpers ----------
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatNumber = (val) => {
    if (val === null || val === undefined || val === "") return "";
    const num = Number(val);
    if (Number.isNaN(num)) return String(val);
    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 3,
    });
  };

  // map API row to grid row using is_active from backend
  const mapApiRowToGridRow = (r) => {
    const raw = (r.is_active ?? "").toString().trim().toUpperCase();

    let status;
    if (["ACTIVE", "A", "Y", "TRUE", "1"].includes(raw)) {
      status = "Active";
    } else if (["INACTIVE", "I", "N", "FALSE", "0"].includes(raw)) {
      status = "InActive";
    } else {
      status = "Active";
    }

    return {
      rate_master_id: r.rate_master_id,
      code_type: r.code_type_name || "",
      code_type_id: r.code_type_id,
      hsn_sac_code: r.hsn_sac_code || "",
      hsn_sac_master_id: r.hsn_sac_master_id,
      effective_date: r.effect_date,
      codedisabled: r.codedisabled,
      is_active: r.is_active,
      status,
    };
  };

  // ---------- API: main list ----------
  const fetchAll = async () => {
    try {
      setIsLoading(true);

      const payload = {
        code_type_id: 0,
        hsn_sac_master_id: 0,
        from_effect_date: null,
        to_effect_date: null,
      };

      const res = await axios.post(
        `${baseUrl}/search-gst-rate-master/`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      const data = res.data?.data || [];
      setRowsSrc(data.map(mapApiRowToGridRow));
      setGstDetails({});
      setLoadingDetails({});
    } catch (err) {
      console.error("Error fetching GST rates:", err);
      toast.error("Failed to load GST Rate Master");
      setRowsSrc([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFiltered = async (payload) => {
    try {
      setIsLoading(true);
      const res = await axios.post(
        `${baseUrl}/search-gst-rate-master/`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );
      const data = res.data?.data || [];
      setRowsSrc(data.map(mapApiRowToGridRow));
      setGstDetails({});
      setLoadingDetails({});
    } catch (err) {
      console.error("Filter error:", err);
      toast.error("Failed to fetch filtered data");
      setRowsSrc([]);
    } finally {
      setIsLoading(false);
    }
  };

  // load full list on mount
  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- API: details (expand row) ----------
  const fetchGstRateDetails = async (rate_master_id) => {
    if (!rate_master_id) return;
    if (gstDetails[rate_master_id]) return; // already loaded

    setLoadingDetails((prev) => ({ ...prev, [rate_master_id]: true }));
    try {
      const res = await axios.get(
        `${baseUrl}/main/get_gst_rate_byrate_master_id`,
        { params: { rate_master_id } }
      );
      const data = res.data?.data || [];
      setGstDetails((prev) => ({ ...prev, [rate_master_id]: data }));
    } catch (err) {
      console.error("Error fetching GST rate details:", err);
      toast.error("Failed to load GST rate details");
      setGstDetails((prev) => ({ ...prev, [rate_master_id]: [] }));
    } finally {
      setLoadingDetails((prev) => ({ ...prev, [rate_master_id]: false }));
    }
  };

  // soft delete handler
  const handleDeleteClick = async (row) => {
    if (!row.rate_master_id || row.rate_master_id === "No Records Found") return;

    const confirmDelete = window.confirm(
      "Are you sure you want to change the status?"
    );
    if (!confirmDelete) return;

    try {
      const payload = {
        rate_master_id: row.rate_master_id,
        modified_by: userObj.username || userObj.user_name || "system",
      };

      await axios.post(
        `${baseUrl}/gst-rate-master-soft-delete/`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      toast.success("Record set to InActive", {
        position: "top-right",
      });

      await fetchAll();
    } catch (err) {
      console.error("Error soft deleting GST rate master:", err);
      toast.error("Failed to delete record", {
        position: "top-right",
      });
    }
  };

  // ---------- Expanded sub-grid ----------
  const ExpandedComponent = ({ data }) => {
    const id = data.rate_master_id;
    const details = gstDetails[id] || [];
   const isRowLoading = loadingDetails[id];

    return (
      <div
        className="expandedTableWrapper"
        style={{
          marginLeft: "60px",
          border: "1px solid #ccc",
          borderRadius: "8px",
          padding: "16px",
          background: "#f9f9f9",
          marginTop: "10px",
          marginBottom: "10px",
        }}
      >
        {isRowLoading ? (
          <div className="text-center py-3">
            <div
              className="spinner-border spinner-border-sm text-primary"
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className="ms-2">Loading GST details...</span>
          </div>
        ) : details.length > 0 ? (
          <div style={{ overflow: "auto" }}>
            <table
              className="table table-bordered table-hover gst-expanded-table"
              style={{
                fontSize: "14px",
                margin: 0,
                minWidth: "100%",
              }}
            >
              <thead>
                <tr>
                  <th>GST Type</th>
                  <th className="text-end">Tax %</th>
                  <th className="text-end">RCM Company %</th>
                  <th className="text-end">RCM Party %</th>
                </tr>
              </thead>
              <tbody>
                {details.map((item, index) => (
                  <tr key={item.rate_master_detail_id || index}>
                    <td>{item.gst_type_name || item.tax_type || "—"}</td>
                    <td className="text-end">
                      {formatNumber(
                        item.tax_percentage ?? item.tax_percent ?? ""
                      )}
                    </td>
                    <td className="text-end">
                      {formatNumber(
                        item.rcm_own_percentage ??
                          item.rcm_company_percent ??
                          ""
                      )}
                    </td>
                    <td className="text-end">
                      {formatNumber(
                        item.rcm_party ?? item.rcm_party_percent ?? ""
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-3" style={{ color: "#6c757d" }}>
            No GST details found
          </div>
        )}
      </div>
    );
  };

  // ---------- columns (with Status + Edit + Delete) ----------
  const columns = useMemo(
    () => [
      {
        name: "Code Type",
        selector: (row) => row.code_type,
        sortable: true,
        cell: (row) => (
          <Tooltip title={row.code_type || ""}>
            <span className="cell-wrap">{row.code_type}</span>
          </Tooltip>
        ),
        minWidth: "150px",
      },
      {
        name: "HSN/SAC Code",
        selector: (row) => row.hsn_sac_code,
        sortable: true,
        cell: (row) => (
          <Tooltip title={row.hsn_sac_code || ""}>
            <span className="cell-wrap">{row.hsn_sac_code}</span>
          </Tooltip>
        ),
        minWidth: "180px",
      },
      {
        name: "Effective Date",
        selector: (row) => row.effective_date,
        sortable: true,
        cell: (row) => {
          const formatted = row.effective_date
            ? formatDate(row.effective_date)
            : "";

          return (
            <Tooltip title={formatted}>
              <span className="cell-wrap">
                {row.rate_master_id === "No Records Found"
                  ? "No Records Found"
                  : formatted}
              </span>
            </Tooltip>
          );
        },
        minWidth: "140px",
      },
      {
        name: "Status",
        selector: (row) => row.status,
        sortable: true,
        cell: (row) => (
          <Tooltip title={row.status || ""}>
            <span className="cell-wrap">{row.status}</span>
          </Tooltip>
        ),
        minWidth: "120px",
      },
      ...(canSubmit
        ? [
            {
              name: "Actions",
              width: "110px",
              button: true,
              ignoreRowClick: true,
              allowOverflow: true,
              cell: (row) => {
                if (row.rate_master_id === "No Records Found") return null;
                return (
                  <div style={{ display: "flex", gap: 8 }}>
                    <Tooltip title="Edit">
                      <span
                        className="commonActionIcons"
                        onClick={() =>
                          navigate("/finance/gst-rate/add", {
                            state: {
                              gstRate: row,
                              rate_master_id: row.rate_master_id,
                            },
                          })
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <MdEdit />
                      </span>
                    </Tooltip>

                    <Tooltip title="Delete">
                      <span
                        className="commonActionIcons"
                        onClick={() => handleDeleteClick(row)}
                        style={{ cursor: "pointer" }}
                      >
                        <MdDelete />
                      </span>
                    </Tooltip>
                  </div>
                );
              },
            },
          ]
        : []),
    ],
    [canSubmit, navigate, gstDetails]
  );

  // ---------- search ----------
  const searchableColumns = [
    (row) => row.code_type,
    (row) => row.hsn_sac_code,
    (row) => (row.effective_date ? formatDate(row.effective_date) : ""),
    (row) => row.status,
  ];

  const filteredRecords = useMemo(() => {
    if (!searchQuery.trim()) return rowsSrc;

    const query = searchQuery.toUpperCase().replace(/[-\s]+/g, "");
    return rowsSrc.filter((item) =>
      searchableColumns.some((selector) => {
        const value = selector(item);
        const stringValue = String(value || "")
          .toUpperCase()
          .replace(/[-\s]+/g, "");
        return stringValue.includes(query);
      })
    );
  }, [rowsSrc, searchQuery]);

  const noRecordsRow = {
    rate_master_id: "No Records Found",
    code_type: "",
    hsn_sac_code: "",
    effective_date: "No Records Found",
    status: "",
  };

  // ---------- Load Code Types for Filter ----------
  const fetchGstCodeTypes = async () => {
    try {
      const resp = await axios.post(baseUrl + "/GetCodeType/");
      const data = Array.isArray(resp.data) ? resp.data : [];
      setCodeTypes(data);
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setCodeTypes([]);
        return;
      }
      console.error(e);
      toast.error("Failed to load GST Code Types");
    }
  };

  useEffect(() => {
    if (baseUrl) fetchGstCodeTypes();
  }, [baseUrl]);

  useEffect(() => {
    const loadHsnSac = async () => {
      if (!form.code_type_id) {
        setHsnSacOptions([]);
        return;
      }

      const selectedCt = codeTypes.find(
        (ct) => String(ct.code_type_id) === String(form.code_type_id)
      );
      const codeTypeValue =
        selectedCt?.code_type || selectedCt?.code_type_name;
      if (!codeTypeValue) {
        setHsnSacOptions([]);
        return;
      }

      setLoadingHSN(true);
      try {
        const resp = await axios.post(
          `${baseUrl}/get_hsn_sac_bycodetype/`,
          { code_type: codeTypeValue },
          { headers: { "Content-Type": "application/json" } }
        );

        if (Array.isArray(resp.data)) {
          setHsnSacOptions(resp.data);
        } else {
          setHsnSacOptions([]);
        }
      } catch (err) {
        console.error(err);
        if (err.response?.status === 404) {
          toast.info("No HSN/SAC codes found for selected Code Type", {
            position: "top-right",
          });
        } else {
          toast.error("Failed to load HSN/SAC codes", {
            position: "top-right",
          });
        }
        setHsnSacOptions([]);
      } finally {
        setLoadingHSN(false);
      }
    };

    if (baseUrl && codeTypes.length > 0) {
      loadHsnSac();
    }
  }, [form.code_type_id, codeTypes, baseUrl]);

  // ---------- filter modal handlers ----------
  const handleFilterSubmit = async (e) => {
    e?.preventDefault?.();

    const payload = {
      code_type_id: form.code_type_id ? Number(form.code_type_id) : 0,
      hsn_sac_master_id: form.hsn_sac_master_id
        ? Number(form.hsn_sac_master_id)
        : 0,
      from_effect_date: form.from_date || null,
      to_effect_date: form.to_date || null,
    };

    await fetchFiltered(payload);
    setShowFilterModal(false);
    setShowAll(false);
  };

  const handleResetFilter = async () => {
    setForm({
      code_type: "",
      code_type_id: "",
      hsn_sac_master_id: "",
      from_date: "",
      to_date: "",
    });
    setHsnSacOptions([]);
    setSearchQuery("");
    setShowAll(true);
    await fetchAll();
  };

  const handleShowAll = async () => {
    setShowAll(true);
    setSearchQuery("");
    await fetchAll();
  };

  return (
    <div className="pageBody">
      <ToastContainer position="top-right" autoClose={3000} newestOnTop />

      {/* Header */}
      <div className="commonDataTableHead">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center" style={{ gap: 10 }}>
            <h6 className="commonTableTitle">GST Rate Master</h6>
          </div>

          <div>
            <input
              type="text"
              placeholder="Search ..."
              value={searchQuery}
              className="searchInput"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="t1">Filter</Tooltip>}
            >
              <Button
                className="secondaryBtn"
                variant="secondary"
                onClick={() => setShowFilterModal(true)}
              >
                <MdFilterList />
              </Button>
            </OverlayTrigger>

            {canSubmit && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="t3">Add</Tooltip>}
              >
                <Button
                  className="primaryBtn"
                  variant="primary"
                  onClick={() => navigate("/finance/gst-rate/add")}
                >
                  <MdAddCircle />
                </Button>
              </OverlayTrigger>
            )}
          </div>
        </div>
      </div>

      {/* DataTable with expandable rows */}
      <div className="commonTable">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (
            <DataTable
              className="custom-table"
              columns={columns}
              data={
                Array.isArray(filteredRecords) && filteredRecords.length > 0
                  ? filteredRecords
                  : [noRecordsRow]
              }
              keyField="rate_master_id"
              expandableRows={
                Array.isArray(filteredRecords) && filteredRecords.length > 0
              }
              expandableRowsComponent={ExpandedComponent}
              expandableRowExpanded={(row) =>
                row.rate_master_id &&
                row.rate_master_id !== "No Records Found" &&
                gstDetails[row.rate_master_id]
              }
              onRowExpandToggled={(expanded, row) => {
                if (
                  expanded &&
                  row.rate_master_id &&
                  row.rate_master_id !== "No Records Found"
                ) {
                  fetchGstRateDetails(row.rate_master_id);
                }
              }}
              expandableRowsHideExpander={false}
              pagination={
                Array.isArray(filteredRecords) && filteredRecords.length > 0
              }
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 20, 50]}
              highlightOnHover
              responsive
              fixedHeader
              fixedHeaderScrollHeight="calc(100vh - 250px)"
              conditionalRowStyles={[
                {
                  when: (row) => row.rate_master_id === "No Records Found",
                  style: {
                    textAlign: "center",
                    fontSize: "16px",
                    color: "red",
                    backgroundColor: "#f9f9f9",
                  },
                },
              ]}
              customStyles={{
                headRow: {
                  style: {
                    backgroundColor: "#f8f9fa",
                    borderBottom: "2px solid #dee2e6",
                    fontWeight: "600",
                  },
                },
                rows: {
                  style: {
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  },
                },
                expanderButton: {
                  style: {
                    cursor: "pointer",
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: "#007bff",
                  },
                },
              }}
            />
          )}
        </div>
      </div>

      {/* Filter Modal */}
      <Modal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        className="commonFilterModal"
      >
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Search GST Rates</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modalBodyScrollable">
          <Form id="filterForm" onSubmit={handleFilterSubmit}>
            <Row>
              {/* Code Type dropdown */}
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group>
                    <Form.Label>Code Type</Form.Label>
                    <Form.Select
                      id="code_type_id"
                      value={form.code_type_id}
                      onChange={(e) => {
                        const idVal = e.target.value;
                        const found = codeTypes.find(
                          (ct) =>
                            String(ct.code_type_id) === String(idVal)
                        );

                        setForm((prev) => ({
                          ...prev,
                          code_type_id: idVal,
                          code_type:
                            found?.code_type ||
                            found?.code_type_name ||
                            "",
                          hsn_sac_master_id: "",
                        }));
                      }}
                    >
                      <option value="">--Select Code Type--</option>
                      {codeTypes.map((ct) => (
                        <option
                          key={ct.code_type_id}
                          value={ct.code_type_id}
                        >
                          {ct.code_type_name || ct.code_type}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </Col>

              {/* HSN/SAC dropdown */}
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group>
                    <Form.Label>HSN/SAC Code</Form.Label>
                    <Form.Select
                      id="hsn_sac_master_id"
                      value={form.hsn_sac_master_id}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          hsn_sac_master_id: e.target.value,
                        }))
                      }
                      disabled={!form.code_type_id || loadingHSN}
                    >
                      <option value="">--Select HSN/SAC--</option>
                      {hsnSacOptions.map((opt) => (
                        <option
                          key={opt.hsn_sac_master_id}
                          value={opt.hsn_sac_master_id}
                        >
                          {opt.hsn_sac_code}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="modalFooterFixed">
          <Button
            variant="secondary"
            className="btn-info clearBtn me-2"
            onClick={handleResetFilter}
          >
            Reset
          </Button>
          <div>
            <Button
              variant="secondary"
              className="btn-danger secondaryBtn me-2"
              onClick={() => setShowFilterModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="btn-success primaryBtn"
              type="submit"
              form="filterForm"
            >
              Search
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GSTRateMaster;
